import * as React from "react";

interface OtpGridProps {
  /** Number of digit boxes. Defaults to 6. */
  length?: number;
  /** Current value as a string (length up to `length`). */
  value: string;
  /** Called whenever the user changes the value. */
  onChange: (next: string) => void;
  /**
   * Called when the user has typed (or pasted) a full-length code.
   * The host should call into the API and may set `error` on failure;
   * the grid will shake and let the user retry.
   */
  onComplete?: (full: string) => void;
  /** Set to true to play the shake animation and dim the boxes. */
  error?: boolean;
  /** Auto-focus the first empty box on mount. */
  autoFocus?: boolean;
  /** Extra class merged onto the wrapper. */
  className?: string;
  /**
   * Base style class for the wrapper. Defaults to `v-field-u__otp` (signup
   * styling). `/signin` passes `v-field-s__otp` so the route can ship a
   * scoped CSS bundle without pulling in signup-field.css.
   */
  baseClass?: string;
}

/**
 * 6-digit OTP grid — paste-aware, keyboard-navigable, with a shake animation
 * for invalid codes. Style is driven by the `baseClass` prop (default
 * `.v-field-u__otp`); the host route ships matching CSS rules.
 */
export function OtpGrid({
  length = 6,
  value,
  onChange,
  onComplete,
  error = false,
  autoFocus = true,
  className,
  baseClass = "v-field-u__otp",
}: OtpGridProps): React.ReactElement {
  const refs = React.useRef<Array<HTMLInputElement | null>>([]);

  // Pad value to grid length so map() always renders `length` boxes.
  const digits = React.useMemo<string[]>(() => {
    const out: string[] = [];
    for (let i = 0; i < length; i++) out.push(value[i] ?? "");
    return out;
  }, [value, length]);

  React.useEffect(() => {
    if (autoFocus) refs.current[0]?.focus();
    // We only want this on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setDigit = (idx: number, raw: string) => {
    const d = raw.replace(/\D/g, "").slice(0, 1);
    const next = [...digits];
    next[idx] = d;
    const joined = next.join("");
    onChange(joined);
    if (d && idx < length - 1) refs.current[idx + 1]?.focus();
    if (joined.length === length && next.every((c) => c)) {
      // Defer to next tick so React commits the value before the host fires
      // the network call.
      setTimeout(() => onComplete?.(joined), 0);
    }
  };

  const onKeyDown = (idx: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Backspace" && !digits[idx] && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowLeft" && idx > 0) {
      refs.current[idx - 1]?.focus();
    } else if (e.key === "ArrowRight" && idx < length - 1) {
      refs.current[idx + 1]?.focus();
    }
  };

  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    const text = e.clipboardData.getData("text");
    const pastedDigits = text.replace(/\D/g, "").slice(0, length);
    if (!pastedDigits) return;
    e.preventDefault();
    onChange(pastedDigits);
    const focusIdx = Math.min(pastedDigits.length, length - 1);
    setTimeout(() => refs.current[focusIdx]?.focus(), 0);
    if (pastedDigits.length === length) {
      setTimeout(() => onComplete?.(pastedDigits), 0);
    }
  };

  return (
    <div
      className={`${baseClass} ${error ? "shake" : ""} ${className ?? ""}`}
      onPaste={onPaste}
    >
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => {
            refs.current[i] = el;
          }}
          inputMode="numeric"
          autoComplete="one-time-code"
          maxLength={1}
          className={d ? "filled" : ""}
          value={d}
          onChange={(e) => setDigit(i, e.target.value)}
          onKeyDown={(e) => onKeyDown(i, e)}
          aria-label={`Digit ${i + 1}`}
        />
      ))}
    </div>
  );
}
